import { RootState, useAppDispatch } from 'store';
import { useWizardContext } from './useWizardContext';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    resetWizard,
    updateStepData,
    updateWizardTitle,
    setMetadata as setMetadataAction,
    setPayload as setPayloadAction,
    setIsSubmitting as setIsSubmittingAction,
} from '../store/wizardSlice';

export function useWizardState<TFormData extends Record<string, any>>() {
    const dispatch = useAppDispatch();
    const { config, onCancel } = useWizardContext();

    const { currentStepId, history, formData, metadata, payload, isSubmitting, isValidating } = useSelector(
        (state: RootState) => state.wizard
    );

    const currentStep = useMemo(() => {
        const step = config.steps[currentStepId];
        if (!step) {
            throw new Error(`Current step "${currentStepId}" not found in the wizard config`);
        }
        return step;
    }, [config, currentStepId]);

    const percentComplete = useMemo(() => {
        if (!config) return 0;

        // If the current step has a defined percentComplete, we can use that.
        if (currentStep.percentComplete !== undefined) {
            return currentStep.percentComplete;
        }

        const stepCount = Array.from(Object.keys(config.steps)).length;
        const completedSteps = Array.from(new Set(history));
        let validStepCount = completedSteps.filter((step) => step in config.steps).length;

        // subtracting 1 so the first step is at 0% and the last step is under 100%
        if (validStepCount > 0) {
            validStepCount -= 1;
        }

        const result = Math.round((validStepCount / stepCount) * 100);

        return result > 100 ? 100 : result;
    }, [config, history, currentStep]);

    const handleReset = () => {
        dispatch(resetWizard());
        if (onCancel) {
            onCancel();
        }
    };

    const updateStepFormData = (data: Partial<TFormData>) => {
        dispatch(updateStepData({ stepId: currentStepId, data }));
    };

    const updateTitle = (title: string) => {
        dispatch(updateWizardTitle(title));
    };

    const setMetadata = (data: any | ((prevMetadata: any) => any)) => {
        let updatedMetadata;

        if (typeof data === 'function') {
            updatedMetadata = data(metadata);
        } else {
            updatedMetadata = data;
        }

        dispatch(setMetadataAction(updatedMetadata));
    };

    const setPayload = (data: any | ((prev: any) => any)) => {
        let payloadData;

        if (typeof data === 'function') {
            payloadData = data(payload);
        } else {
            payloadData = data;
        }

        dispatch(setPayloadAction(payloadData));
    };

    const setIsSubmitting = (value: boolean) => {
        dispatch(setIsSubmittingAction(value));
    };

    return {
        percentComplete,
        formData,
        currentStep,
        metadata,
        isSubmitting,
        isValidating,
        handleReset,
        updateStepFormData,
        updateTitle,
        setMetadata,
        setPayload,
        setIsSubmitting,
    };
}
